import { SmartDatePicker } from 'app/components/shared/FormComponents/SmartDatePicker'
import { SmartInput } from 'app/components/shared/FormComponents/SmartInput'
import { Button } from 'app/components/ui/Button'
import { Text } from 'app/components/ui/Text'
import { SponsorRequestTimeline } from 'EstraSponsor'
import React, { useEffect } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import styled, { useTheme } from 'styled-components'

import {
  SponsorRequestEventsFormData,
  sponsorRequestEventDefaultValues,
} from '../form'

export const SponsorRequestDataInnerFormEvents = (): JSX.Element => {
  const theme = useTheme()

  const { watch, setValue, getValues } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    name: 'sponsor_request_events',
  })

  const timelineWatcher = watch('timeline') as SponsorRequestTimeline | null

  // If is a single timeline only one event is allowed
  const isSingleTimeline = timelineWatcher === 'single'

  // Sync sponsor request events with timeline changes
  // Timeline -> single -> Only one event
  // Timeline -> season -> At least one event
  // Timeline -> multiple -> At least two event
  useEffect(() => {
    const events = getValues(
      'sponsor_request_events'
    ) as SponsorRequestEventsFormData[]

    switch (timelineWatcher) {
      case 'single':
        if (events.length === 0) append(sponsorRequestEventDefaultValues)
        if (events.length > 1) setValue('sponsor_request_events', [events[0]])
        break

      case 'season':
        if (events.length === 0) append(sponsorRequestEventDefaultValues)
        break

      case 'multiple':
        if (events.length === 0) {
          append([
            sponsorRequestEventDefaultValues,
            sponsorRequestEventDefaultValues,
          ])
        }
        if (events.length === 1) append(sponsorRequestEventDefaultValues)
        break
      default:
        setValue('sponsor_request_events', [])
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineWatcher])

  // Show events only when a timeline has been selected
  return timelineWatcher ? (
    <>
      <EventsHeader>
        <Text variant='s14' color={theme.color.primari.grigioEstra}>
          N.B: le date dell&apos;evento devono essere almeno 1 mese successive
          alla data odierna.
        </Text>
        {!isSingleTimeline && (
          <Button
            onClick={(e) => {
              // Avoid validation trigger
              e.preventDefault()
              e.stopPropagation()
              append(sponsorRequestEventDefaultValues)
            }}
          >
            Aggiungi evento
          </Button>
        )}
      </EventsHeader>

      <EventItemsWrapper>
        {fields.map((field, index: number) => {
          const fieldName = `sponsor_request_events[${index}]`
          return (
            <EventItemCard
              key={field.id}
              data-testid={'sponsor_request_event-item'}
            >
              <EventGrid>
                <SmartDatePicker
                  label='Data di inizio'
                  name={`${fieldName}.start_on`}
                  minDate={(() => {
                    // Minimum date is 30 days from now
                    const today = new Date()
                    today.setDate(today.getDate() + 30)
                    return today
                  })()}
                  maxDate={watch(`${fieldName}.end_on`)}
                  onChange={() => {
                    if (!getValues(`${fieldName}.start_on`))
                      setValue(`${fieldName}.end_on`, null)
                  }}
                />
                <SmartDatePicker
                  label='Data di fine'
                  name={`${fieldName}.end_on`}
                  minDate={watch(`${fieldName}.start_on`)}
                  disabled={!watch(`${fieldName}.start_on`)}
                />
                <SmartInput
                  label='Numero spettatori'
                  type='text'
                  name={`${fieldName}.attendees`}
                />
                {!isSingleTimeline && (
                  <RemoveButtonWrapper>
                    <Button
                      onClick={(e) => {
                        // Avoid validation trigger
                        e.preventDefault()
                        e.stopPropagation()
                        remove(index)
                      }}
                      disabled={
                        timelineWatcher === 'multiple'
                          ? fields.length === 2
                          : fields.length === 1
                      }
                      variant='white'
                    >
                      Rimuovi
                    </Button>
                  </RemoveButtonWrapper>
                )}
              </EventGrid>
            </EventItemCard>
          )
        })}
      </EventItemsWrapper>
    </>
  ) : (
    <></>
  )
}

const EventsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-flow: wrap-reverse;
  row-gap: 2rem;

  margin-bottom: 2rem;
`

const EventItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const EventItemCard = styled.div`
  background: #e9e9e9;
  border-radius: 1rem;
  padding: 2rem;
`

const EventGrid = styled.div`
  display: grid;
  gap: 2rem;

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    grid-template-columns: 1fr 1fr 1fr auto;
  }
`

const RemoveButtonWrapper = styled.div`
  align-self: center;
  text-align: end;
`
